/**
 * Optimized script loading for better performance
 * Handles non-critical JavaScript loading and execution
 */
(function() {
  'use strict';

  // Track loaded scripts to avoid duplicates
  const loadedScripts = new Set();
  
  // Queue for deferred operations
  const deferredQueue = [];
  
  // Performance timing
  const performanceTimer = {
    start: performance.now(),
    loadComplete: null,
    scriptsLoaded: 0
  };

  /**
   * Load script asynchronously with error handling
   */
  function loadScript(src, callback) {
    if (loadedScripts.has(src)) {
      callback && callback();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.defer = true;
    
    script.onload = function() {
      loadedScripts.add(src);
      performanceTimer.scriptsLoaded++;
      callback && callback();
    };
    
    script.onerror = function() {
      console.warn('Failed to load script:', src);
      callback && callback(new Error('Script load failed'));
    };
    
    document.head.appendChild(script);
  }

  /**
   * Load multiple scripts in sequence
   */
  function loadScripts(scripts, callback) {
    let loaded = 0;
    const total = scripts.length;
    
    if (total === 0) {
      callback && callback();
      return;
    }
    
    function onScriptLoad() {
      loaded++;
      if (loaded === total) {
        callback && callback();
      }
    }
    
    scripts.forEach(script => {
      loadScript(script, onScriptLoad);
    });
  }

  /**
   * Initialize non-critical scripts after page load
   */
  function initNonCriticalScripts() {
    // Scripts that can be loaded after initial page load
    const nonCriticalScripts = [
      // Add non-critical script URLs here
    ];
    
    // Load after user interaction or after delay
    let scriptsLoaded = false;
    
    function loadNonCritical() {
      if (scriptsLoaded) return;
      scriptsLoaded = true;
      
      loadScripts(nonCriticalScripts, function() {
        console.log('Non-critical scripts loaded');
      });
    }
    
    // Trigger loading on user interaction
    const interactionEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    interactionEvents.forEach(event => {
      document.addEventListener(event, loadNonCritical, { once: true, passive: true });
    });
    
    // Fallback: load after 3 seconds
    setTimeout(loadNonCritical, 3000);
  }

  /**
   * Optimize images not handled by native lazy loading
   */
  function optimizeImages() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            const img = entry.target;
            
            // Handle data-src images
            if (img.dataset.src && !img.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
            
            // Handle srcset
            if (img.dataset.srcset && !img.srcset) {
              img.srcset = img.dataset.srcset;
              img.removeAttribute('data-srcset');
            }
            
            // Add fade-in effect
            img.classList.add('loaded');
            
            imageObserver.unobserve(img);
          }
        });
      }, {
        rootMargin: '50px 0px',
        threshold: 0.01
      });

      // Observe images with data-src
      document.querySelectorAll('img[data-src], img[data-srcset]').forEach(function(img) {
        imageObserver.observe(img);
      });
    }
  }

  /**
   * Optimize form interactions
   */
  function optimizeForms() {
    // Cache form elements for better performance
    const forms = document.querySelectorAll('form');
    
    forms.forEach(function(form) {
      // Optimize search forms
      if (form.classList.contains('search-form')) {
        const searchInput = form.querySelector('input[type="search"]');
        if (searchInput) {
          // Debounced search handling
          let searchTimeout;
          searchInput.addEventListener('input', function(e) {
            clearTimeout(searchTimeout);
            searchTimeout = setTimeout(function() {
              // Handle search input
              console.log('Search input optimized');
            }, 300);
          });
        }
      }
    });
  }

  /**
   * Optimize third-party integrations
   */
  function optimizeThirdParty() {
    // Delay third-party scripts that aren't critical
    const thirdPartyScripts = document.querySelectorAll('script[data-third-party]');
    
    thirdPartyScripts.forEach(function(script) {
      const originalSrc = script.src || script.dataset.src;
      script.removeAttribute('src');
      
      // Load after user interaction
      function loadThirdParty() {
        script.src = originalSrc;
      }
      
      document.addEventListener('scroll', loadThirdParty, { once: true, passive: true });
      setTimeout(loadThirdParty, 5000); // Fallback
    });
  }

  /**
   * Monitor performance
   */
  function monitorPerformance() {
    if ('PerformanceObserver' in window) {
      // Monitor Largest Contentful Paint
      try {
        const observer = new PerformanceObserver(function(list) {
          const entries = list.getEntries();
          entries.forEach(function(entry) {
            if (entry.entryType === 'largest-contentful-paint') {
              console.log('LCP:', entry.startTime);
            }
          });
        });
        
        observer.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        // Silently handle if not supported
      }
    }
  }

  /**
   * Initialize all optimizations
   */
  function init() {
    performanceTimer.loadComplete = performance.now();
    
    console.log('Page load optimization initialized', {
      loadTime: performanceTimer.loadComplete - performanceTimer.start,
      scriptsLoaded: performanceTimer.scriptsLoaded
    });
    
    optimizeImages();
    optimizeForms();
    optimizeThirdParty();
    initNonCriticalScripts();
    monitorPerformance();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();